import clsx from "clsx";
import { useState } from "react";
import { Outlet } from "react-router-dom";

import { PlatformHeader } from "./PlatformHeader";
import { PlatformSidebar } from "./PlatformSidebar";

import { useWorkspaceViewport } from "@/components/layouts/useWorkspaceViewport";

/**
 * Shell for the Platform Owner console.
 *
 * Mirrors `DashboardLayout`'s proven mechanics (collapse state, mobile drawer + backdrop, content
 * offset) but composes the platform sidebar/header instead. It deliberately does not reuse
 * `DashboardLayout` itself: that mounts `Sidebar` -> `useSidebarState`, which resolves tenant
 * branding, tenant permissions and the tenant feature registry — none of which a PLATFORM-scope
 * session has.
 */
export function PlatformOwnerLayout() {
  const pageRef = useWorkspaceViewport<HTMLElement>();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <PlatformSidebar
        collapsed={collapsed}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuClose={() => setIsMobileMenuOpen(false)}
      />

      <div
        className={clsx(
          "min-h-screen transition-all duration-300 ease-in-out",
          collapsed ? "lg:ml-20" : "lg:ml-64",
        )}
      >
        <PlatformHeader
          onSidebarToggle={() => setCollapsed((value) => !value)}
          onMobileMenuToggle={() => setIsMobileMenuOpen(true)}
        />

        <main ref={pageRef} className="workspace-page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
